import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { X } from 'lucide-react';
import {
    Combobox,
    ComboboxContent,
    ComboboxList,
    ComboboxItem,
    ComboboxFieldInput,
} from '@/Components/shadcn/combobox';
import { Field, FieldError, FieldLabel } from '@/Components/shadcn/field';
import { Spinner } from '@/Components/shadcn/spinner';
import { cn } from '@/lib/utils';
import { LocationProvider, useLocationContext } from './LocationContext';
import useGooglePlacesAutocomplete from './hooks/useGooglePlacesAutocomplete';
import LocationDropdownOption from './Components/LocationDropdownOption';
import GooglePoweredFooter from './Components/GooglePoweredFooter';
import parseWithKey from './Utils/parseWithKey';
import type { MapsLegacyLocationTypes } from './types';

export interface RHFAutocompleteLocationProps {
    name: string;
    label: string | React.JSX.Element;
    locationType?: MapsLegacyLocationTypes;
    noOptionsText?: React.ReactNode;
    placeholder?: string;
}

export function RHFAutocompleteLocation(props: RHFAutocompleteLocationProps): React.JSX.Element {
    const { control } = useFormContext();
    const { field } = useController({ name: props.name, control });

    return (
        <LocationProvider initialLocationDetails={field.value}>
            <RHFAutocompleteLocationInner {...props} />
        </LocationProvider>
    );
}

function RHFAutocompleteLocationInner({
    name,
    label,
    locationType,
    noOptionsText,
    placeholder,
}: RHFAutocompleteLocationProps): React.JSX.Element {
    const { control, setValue, trigger } = useFormContext();
    const { field, fieldState } = useController({ name, control });

    const { inputValue, locationDetails, setInputValue, setLocationDetails, clear, reset } = useLocationContext();
    const { options, loading, fetchPredictions, selectPrediction } = useGooglePlacesAutocomplete();

    const [comboboxOpen, setComboboxOpen] = useState(false);
    const [dropDirection, setDropDirection] = useState<'bottom' | 'top'>('bottom');
    const [portalRoot, setPortalRoot] = useState<HTMLDivElement | null>(null);
    const [pickNonce, setPickNonce] = useState(0);

    const anchorRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const resolvedLocationType = locationType ?? 'cities';

    useEffect(() => {
        if (inputValue === '') {
            setValue(name, null);
        }
    }, [inputValue, name, setValue]);

    useEffect(() => {
        if (locationDetails != null) {
            setValue(name, locationDetails, { shouldValidate: true });
        } else {
            setValue(name, null);
        }
    }, [locationDetails, name, setValue]);

    useEffect(() => {
        if (!comboboxOpen || !anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        const dropdownMaxHeight = 240 + 8;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        setDropDirection(spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow ? 'top' : 'bottom');
    }, [comboboxOpen, options.length]);

    const handleInputChange = useCallback(
        (value: string) => {
            if (value === '') {
                clear();
            } else {
                setInputValue(value);
            }
            fetchPredictions(value, resolvedLocationType);
        },
        [clear, setInputValue, fetchPredictions, resolvedLocationType],
    );

    const handleSelect = useCallback(
        (option: NonNullable<unknown>) => {
            const prediction = option as (typeof options)[number];
            selectPrediction(prediction, resolvedLocationType, (details) => {
                if (details) {
                    setLocationDetails(details);
                    setInputValue(details.description);
                } else {
                    clear();
                }
            });
            reset();
            setComboboxOpen(false);
            setPickNonce((n) => n + 1);
        },
        [selectPrediction, resolvedLocationType, setLocationDetails, clear, reset],
    );

    const handleBlur = useCallback(() => {
        trigger(name);
    }, [trigger, name]);

    const handleClear = useCallback(() => {
        clear();
        setInputValue('');
        setValue(name, null);
        setPickNonce((n) => n + 1);
        trigger(name);
    }, [clear, setInputValue, setValue, name, trigger]);

    const onComboboxOpenChange = useCallback(
        (open: boolean) => {
            setComboboxOpen(open);
            if (!open) {
                reset();
                handleBlur();
            }
        },
        [reset, handleBlur],
    );

    const showList = comboboxOpen && (loading || options.length > 0);

    const setContainerRef = useCallback((node: HTMLDivElement | null) => {
        setPortalRoot(node);
    }, []);

    const listBody =
        loading && options.length === 0 ? (
            <div className="text-muted-foreground flex items-center justify-center gap-2 py-4 text-sm">
                <Spinner className="h-4 w-4" />
                Loading...
            </div>
        ) : (
            options.map((option) => {
                const matches = option.mainTextMatchedSubstrings || [];
                const parts = parseWithKey(
                    option.mainText,
                    matches.map((match: any) => [match.offset, match.offset + match.length]),
                );
                return (
                    <ComboboxItem key={option.locationId} value={option} className="cursor-pointer px-2 py-2">
                        <LocationDropdownOption parts={parts} option={option} />
                    </ComboboxItem>
                );
            })
        );

    return (
        <Field className="w-full" data-invalid={fieldState.invalid ? true : undefined}>
            {label && <FieldLabel>{label}</FieldLabel>}
            <div ref={setContainerRef} className="relative w-full">
                <div ref={anchorRef} className="w-full">
                    <Combobox
                        key={pickNonce}
                        modal={false}
                        items={options}
                        filter={null}
                        open={showList}
                        onOpenChange={onComboboxOpenChange}
                        inputValue={inputValue}
                        onInputValueChange={(value, eventDetails) => {
                            if (eventDetails.reason === 'item-press') {
                                setInputValue(value);
                            } else {
                                handleInputChange(value);
                            }
                        }}
                        defaultValue={null}
                        value={field.value}
                        onValueChange={(v) => {
                            if (v != null) {
                                handleSelect(v);
                            }
                        }}
                        itemToStringLabel={(o) =>
                            o == null ? '' : ((o as (typeof options)[number]).description ?? '')
                        }
                        isItemEqualToValue={(a, b) =>
                            (a as (typeof options)[number] | null)?.locationId ===
                            (b as (typeof options)[number] | null)?.locationId
                        }
                    >
                        <div
                            data-cy="jobLocationTextField"
                            className={cn(
                                'border-input bg-card ring-offset-background flex h-10 w-full items-center rounded-xl border px-3 text-sm transition-colors duration-200',
                                'focus-within:border-primary focus-within:ring-primary focus-within:ring-1 focus-within:ring-offset-0',
                                fieldState.invalid &&
                                    'border-destructive focus-within:border-destructive focus-within:ring-destructive',
                            )}
                        >
                            <ComboboxFieldInput
                                ref={inputRef}
                                type="text"
                                placeholder={placeholder}
                                className="placeholder:text-muted-foreground m-0 flex w-full border-0 bg-transparent p-0 text-sm leading-none shadow-none outline-none"
                                autoComplete="off"
                                onBlur={handleBlur}
                            />
                            {inputValue && (
                                <button
                                    type="button"
                                    onClick={handleClear}
                                    className="text-muted-foreground hover:text-foreground ml-1 flex shrink-0 items-center justify-center rounded-sm p-0.5 transition-colors"
                                    aria-label="Clear location"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        <ComboboxContent
                            container={portalRoot ?? undefined}
                            anchor={anchorRef}
                            side={dropDirection === 'bottom' ? 'bottom' : 'top'}
                            sideOffset={4}
                            align="start"
                            className="border-border bg-popover shadow-soft-md max-h-81.5 min-w-[250px] border p-0"
                        >
                            <ComboboxList className="max-h-75">
                                {listBody}
                                {options.length === 0 && !loading && noOptionsText && (
                                    <div className="text-muted-foreground flex justify-center py-2 text-sm">
                                        {noOptionsText}
                                    </div>
                                )}
                            </ComboboxList>
                            <GooglePoweredFooter />
                        </ComboboxContent>
                    </Combobox>
                </div>
            </div>
            <FieldError>{fieldState.error?.message ? String(fieldState.error.message) : null}</FieldError>
        </Field>
    );
}
